<script>
    import WidgetBox from './WidgetBox.svelte';
    import FeatureTab from './sequencer/FeatureTab.svelte';
    import FeatureStatus from './sequencer/FeatureStatus.svelte';
    import FeatureTabPicker from './sequencer/FeatureTabPicker.svelte';
    import SequencerPresets from './sequencer/SequencerPresets.svelte';
    import BuildOutput from './sequencer/BuildOutput.svelte';
    import ExampleButton from './util/ExampleButton.svelte';
    import { pin } from './pincelate-store.js';

    import { slide } from 'svelte/transition';
    import { onMount, tick } from 'svelte';
import FeatureTrack from './sequencer/FeatureTrack.svelte';

    let frame = async() => new Promise(resolve => { requestAnimationFrame(resolve); });

    const gridWidth = 8;
    let result = '';
    let selectWidget;
    
    let decodingInProgress = false;

    const featLayout = [
        ['Consonant', [
            ['frc', 'fricative', "Airflow is partially blocked, like SH", "basic"],
            ['stp', 'stop', "Airflow stopped entirely, like K or P", "basic"],
            ['nas', 'nasal', "Air goes out the nose, not the mouth", "basic"],
            ['vcd', 'voiced', "Vocal cords vibrating: B, D, G", "basic"],
            ['vls', 'voiceless', "Vocal cords don't vibrate, like P, T, K", "basic"],
            ['alv', 'alveolar', "Tongue's right behind the teeth: T, D, S", "basic"],
            ['blb', 'bilabial', "Lips are together", "basic"],
            ['vel', 'velar', "Tongue is against the soft palate: K, G", "basic"],
            ['apr', 'approximant', 'Like L and R', "adv"],
            ['dnt', 'interdental', "Tongue's between the teeth, like TH", "adv"],
            ['glt', 'glottal', "Way back down in your throat, H as in 'HOT'", "adv"],
            ['lat', 'lateral', "Tongue's flat along your teeth, like L", "adv"],
            ['lbd', 'labiodental', "Teeth are on the lips (F, V)", "adv"],
            ['lbv', 'labiovelar', "Lips and tongue move together, like in W", "adv"],
            ['pal', 'palatal', "Tongue moving along the palate, like in Y", "adv"],
            ['pla', 'pal-alv', "Just the sound SH", "adv"]
        ]],
        ['Vowel', [
            ['hgh', 'high', "Tongue at top of mouth: EE and OO", "basic"],
            ['mid', 'mid', "Tongue's halfway up, only for unstressed vowels", "basic"],
            ['low', 'low', "Tongue's low in the mouth, as in 'cat'", "basic"],
            ['fnt', 'front', "Tip of the tongue is up front: heat, hit", "basic"],
            ['cnt', 'central', "Relaxed tongue, for unstressed vowels", "basic"],
            ['bck', 'back', "Tongue's in the back of the mouth: boot, boat, bought", "basic"],
            ['smh', 'semi-high', "Tongue a bit lower than 'high'—think 'bit' and 'book'", "adv"],
            ['umd', 'upper-mid', "Only found in a few vowels ('ow' and 'er')", "adv"],
            ['lmd', 'low-mid', "Tongue position for 'ay' as in 'hay', 'oh' as in 'boat'", "adv"],
            ['rzd', 'rhoticized', "Tongue is bunched up for 'errrr': bird, herd", "adv"],
            ['rnd', 'rounded', "Lips are rounded: boot, boat", "adv"],
            ['unr', 'unrounded', "Lips are smiling: beet, bait", "adv"],
            ['vwl', 'vowel', "Anything that's a vowel has this feature", "adv"]
        ]]
    ];

    let feats = [];
    for (let section of featLayout) {
        for (let [feat, label] of section[1]) {
            feats.push(feat);
        }
    }

    let featureData = {};

    $: console.log(featureData);

    function featureDataClear() {
        featureData = {};
        for (let feat of feats) {
            let arr = [];
            for (let i = 0; i < gridWidth; i++) {
                arr.push(false);
            }
            featureData[feat] = arr;
        }
        console.log("cleared featureData!", featureData);
    }

    function loadPreset(featList) {
        console.log(featList);
        featureDataClear();
        console.log("in loadPreset", featureData);
        for (let i = 0; i < featList.length; i++) {
            for (let f of featList[i]) {
                featureData[f][i] = true;
            }
        }
    }

    async function buildWrap() {
        result = '';
        decodingInProgress = true;
        await tick();
        setTimeout(async function() {
            await build();
            decodingInProgress = false;
            await tick();
        }, 100);
    }

    async function build() {
        result = '';
        let word = [['beg']];
        for (let i = 0; i < gridWidth; i++) { // timestep
            let current = [];
            let allFalse = true;
            for (let j = 0; j < feats.length; j++) { // features
                if (featureData[feats[j]][i]) {
                    current.push(feats[j]);
                    allFalse = false;
                }
            }
            if (allFalse) {
                word.push(['end'])
                break;
            }
            else {
                word.push(current);
            }
        }
        console.log(word);
        const featVec = $pin.featuresToVec([word], $pin.maxLen);
        console.log(featVec);
        for await (let timestep of $pin.spellFeaturesAsync(featVec, 0.35)) {
            result += timestep[0];
            await frame();
            console.log(timestep);
        }
    }

    function featureDataRandomize() {
        featureData = {};
        let phonCount = 3 + Math.floor(Math.random()*(gridWidth-3));
        for (let feat of feats) {
            let arr = [];
            for (let i = 0; i < gridWidth; i++) {
                if (i < phonCount) {
                    arr.push([true, false, false, false][Math.floor(Math.random()*4)]);
                }
                else {
                    arr.push(false);
                }
            }
            featureData[feat] = arr;
        }
        console.log(featureData);
    }

    let selectedTabIdx = 0;

    featureDataClear();

</script>

<style>

</style>

<WidgetBox on:reset={() => { featureDataClear(); selectWidget.reset()}} >

    <span slot="title">Sequencer</span>
    <span slot="tagline">Invent new words by sequencing mouth movements</span>

    <div slot="widget" id="build-a-word">

        <SequencerPresets {decodingInProgress}
            on:preset={(event) => loadPreset(event.detail.featList)}
            on:randomize={featureDataRandomize} 
            bind:this={selectWidget} />

        <!-- svelte-ignore missing-declaration -->
        <FeatureTabPicker {featLayout} bind:selectedTabIdx />

        <div class="box light lev1"
            style="position: relative; z-index: 1; padding-left: 0; padding-right: 0;" transition:slide|local>

            <FeatureStatus {featureData} />
            {#key selectedTabIdx}
                <FeatureTab bind:featureData section={featLayout[selectedTabIdx]} />
            {/key}
                
        </div>

        <BuildOutput on:build={buildWrap} {decodingInProgress} bind:result />

    </div>

    <div slot="modalContent">

        <p>The <b>Sequencer</b> spells words from virtual mouth movements that you put
        in sequence, like a drum machine.</p>

        <h4>How to use it</h4>
        <ul>
            <li>Each row in the grid represents a kind of mouth movement, and
            each column represents a step. Hit a cell in the grid to toggle a mouth movement at a particular
            step.</li>
            <li>Hitting <ExampleButton src="/assets/svg/_all-small-red.svg" alt="All"></ExampleButton> 
            fills an entire row, and <ExampleButton src="/assets/svg/_x-small-red.svg" alt="Clear"></ExampleButton>
            clears the row.</li>
            <li>Toggle between mouth movements associated with consonants and vowels
            with the tabs above the grid.</li>
            <li>When you're done, hit
            <ExampleButton src="/assets/svg/_action-large-red.svg" alt="Action">Play</ExampleButton> to spell
            a word from the contents of the grid.</li>
        </ul>
            
        <h4>Tips and tricks</h4>
        <ul>
            <li>Need inspiration? You can fill the grid with a preset
            sequence with the <b>Examples</b> dropdown menu.</li>
            <li>The
            <ExampleButton src="/assets/svg/_randomize-medium-red.svg" alt="Random">Randomize</ExampleButton>
            button clears the grid and fills it with random values.</li>
            <li>Make sure to hit <b>Advanced</b> to reveal mouth movements that are hidden by default.</li>
            <li>Tap or hover over each row's label for a description of what mouth movement that row controls.</li>
        </ul>
            
    </div>

</WidgetBox>

